import { Icon } from "@portal/lib/icons/icons";
import { AmountMaybeWithMultiCurrencySplit } from "@portal/types/multi-currency";
import { OutstandingAmount } from "@portal/types/snapshot";
import { Divider } from "../Divider/AntDivider";
import { Flex } from "../Flex/Flex";
import { Col, Row } from "../Grid/AntGrid";
import { Popover } from "../PopOver/AntPopOver";
import { TypographyText } from "../Typography/AntTypography";
import { Amount } from "./Amount";
import { overlayStyles } from "./AmountWithCurrencySplit";

const t = {
  openInvoiceBalances: "Open Invoice Balances",
  unappliedCredits: "Unapplied Credits",
  unappliedPayments: "Unapplied Payments",
  unpostedPayments: "Unposted Payments",
  appliedPayments: "Applied Payments",
  totalBalance: "Total",
  taxes: "Taxes",
  otherCharges: "Other Charges",
};
export interface AmountInvoiceCountCustomerCount {
  amount: AmountMaybeWithMultiCurrencySplit;
  invoice_count?: number;
  customer_count?: number;
}
export interface CollectionDetails {
  total_payment_amount: AmountInvoiceCountCustomerCount;
  applied_payment_amount?: AmountMaybeWithMultiCurrencySplit;
  un_applied_payment_amount?: AmountMaybeWithMultiCurrencySplit;
  taxes?: AmountMaybeWithMultiCurrencySplit;
  other_charges?: AmountMaybeWithMultiCurrencySplit;
}

interface AppliedPaymentsCreditsProps {
  appliedPayments: CollectionDetails;
  infoIcon?: React.ReactNode;
}

interface IconProps extends Partial<React.ComponentProps<typeof Icon>> {
  style?: React.CSSProperties;
}

export function InfoIconRegular(props: IconProps) {
  return (
    <Icon color="var(--geekblue-6)" {...props} icon={["far", "info-circle"]} />
  );
}

function UnappliedPaymentsCreditsPopover({
  outstanding,
}: UnappliedPaymentsCreditsProps) {
  const {
    outstanding_amount,
    open_invoices_balance_amount,
    un_applied_credit_amount,
    un_applied_payment_amount,
  } = outstanding;

  const totalOutstandingAmount = (
    <>
      <Divider style={{ margin: 0 }} />
      {outstanding_amount && (
        <AmountSplit amount={outstanding_amount} label={t.totalBalance} bold />
      )}
    </>
  );

  return (
    <Flex space={'12'} vertical>
      {open_invoices_balance_amount && (
        <AmountSplit
          amount={open_invoices_balance_amount.amount}
          label={t.openInvoiceBalances}
        />
      )}
      {un_applied_credit_amount && (
        <AmountSplit
          amount={un_applied_credit_amount.amount}
          label={t.unappliedCredits}
          negative={true}
        />
      )}
      {un_applied_payment_amount && (
        <AmountSplit
          amount={un_applied_payment_amount.amount}
          label={t.unappliedPayments}
          negative={true}
        />
      )}
      {totalOutstandingAmount}
    </Flex>
  );
}
function AppliedPaymentsCreditsPopover({
  appliedPayments,
}: AppliedPaymentsCreditsProps) {
  const {
    total_payment_amount,
    applied_payment_amount,
    un_applied_payment_amount,
    taxes,
    other_charges,
  } = appliedPayments;

  return (
    <Flex space="12" vertical>
      {applied_payment_amount && (
        <AmountSplit
          amount={applied_payment_amount}
          label={t.appliedPayments}
        />
      )}
      {un_applied_payment_amount && (
        <AmountSplit
          amount={un_applied_payment_amount}
          label={t.unappliedPayments}
        />
      )}
      {taxes && <AmountSplit amount={taxes} label={t.taxes} />}
      {other_charges && (
        <AmountSplit amount={other_charges} label={t.otherCharges} />
      )}
      <Divider style={{ margin: 0 }} />
      {total_payment_amount.amount && (
        <AmountSplit
          amount={total_payment_amount.amount}
          label={t.totalBalance}
          bold
        />
      )}
    </Flex>
  );
}

interface AmountSplitProps {
  label: string;
  amount: AmountMaybeWithMultiCurrencySplit;
  bold?: boolean;
  negative?: boolean;
  count?: number;
}

function AmountSplit({
  label,
  amount,
  bold,
  negative,
  count,
}: AmountSplitProps) {
  const amountToDisplay = {
    ...amount,
  };
  return (
    <Row justify="space-between" style={{ width: "100%" }}>
      <Col span={12}>
        <TypographyText strong={bold}>
          {label} {count ? `(${count})` : ""}
        </TypographyText>
      </Col>
      <Col
        span={12}
        style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
      >
        <Flex space="12">
          <TypographyText>{negative ? `(-)` : null}</TypographyText>
          <Amount
            style={{ fontWeight: bold ? "var(--fs-semibold)" : "normal" }}
            amount={amountToDisplay.value}
            currency={amountToDisplay.currency}
            
          />
        </Flex>
      </Col>
    </Row>
  );
}

interface UnappliedPaymentsCreditsProps {
  outstanding: OutstandingAmount;
  unposted?: {
    amount: AmountMaybeWithMultiCurrencySplit;
    paymentCount: number;
  };
  infoIcon?: React.ReactNode;
}

export function UnappliedPaymentsCredits({
  outstanding,
  infoIcon,
}: UnappliedPaymentsCreditsProps) {
  return (
    <Popover
      destroyTooltipOnHide
      placement="rightTop"
      arrow={false}
      overlayInnerStyle={{
        background: "transparent",
        padding: "var(--space-20) var(--space-16)",
      }}
      overlayStyle={{ ...overlayStyles, width: "340px" }}
      content={<UnappliedPaymentsCreditsPopover outstanding={outstanding} />}
    >
      {infoIcon ?? <InfoIconRegular />}
    </Popover>
  );
}

export function AppliedPaymentsCredits(props: AppliedPaymentsCreditsProps) {
  return (
    <Popover
      placement="rightTop"
      content={
        <AppliedPaymentsCreditsPopover
          appliedPayments={props.appliedPayments}
        />
      }
      overlayInnerStyle={{ background: "transparent", padding: 0 }}
      overlayStyle={overlayStyles}
    >
      {props.infoIcon ?? <InfoIconRegular />}
    </Popover>
  );
}
